<template>
  <div>
    <div
      v-if="hasFilterBar"
      class="flex flex-col space-y-4 md:flex-row md:items-center md:space-x-8 md:space-y-0"
      :class="{
        'md:justify-between': tags.length,
        'md:justify-end': !tags.length
      }"
    >
      <ContentExperienceTags
        v-if="tags.length"
        v-model="selectedTag"
        :tags="tags"
      />
      <ContentExperienceSearchInput
        v-if="searchable"
        v-model="searchTerm"
        :placeholder="searchPlaceholder"
      />
    </div>

    <div
      v-auto-animate
      class="grid gap-8 md:grid-cols-2 lg:grid-cols-3"
      :class="{ 'mt-8': hasFilterBar }"
    >
      <ContentExperiencesGridLoading v-if="isLoading" />
      <ContentExperienceCard
        v-for="collection in pagedExperiences"
        v-else-if="hasExperiences"
        :key="collection.id"
        :collection="collection"
        :hide-partner="hidePartner"
        :all-new="allNew"
      />
      <ContentExperiencesGridEmpty
        v-else
        :search-term="searchTerm"
        :error
      />
    </div>

    <PaginationNew
      v-model="page"
      class="mt-8"
      :total-items="filteredExperiences.length"
      :total-pages="totalPages"
      :per-page="perPage"
    />
  </div>
</template>

<script setup lang="ts">
import type { LaravelPaginatedResponse } from '~/types/api'
import type { ContentExperience } from '~/types/content'

const {
  endpoint,
  perPage,
  hidePartner,
  searchable = false,
  searchPlaceholder,
  allNew = false
} = defineProps<{
  endpoint: string
  perPage: number
  hidePartner?: boolean
  searchable?: boolean
  searchPlaceholder?: string
  allNew?: boolean
}>()

const page = ref(1)

const { data, status, error } = useLazyAsyncData(endpoint, () => $larafetch<LaravelPaginatedResponse<ContentExperience[]>>(`/${endpoint}`))
const { data: allTags } = useLazyAsyncData(`${endpoint}/tags`, () => $larafetch(`partners/${useRoute().params.partner_slug}/experiences/tags`))

const tagPositionsMap = computed(() => new Map(allTags.value?.data?.map(tag => [tag.name, tag.position])))

// Loading until we have either data or an error, so switching tabs shows the skeleton immediately rather than the previous tab's results.
const isLoading = computed(() => status.value === 'pending' || (!data.value && !error.value))

const tags = computed(() => {
  const tags = new Set<string>()

  data.value?.data?.forEach((collection) => {
    collection.tags?.forEach((tag) => {
      tags.add(tag)
    })
  })

  return Array.from(tags).sort((a, b) => {
    const positionA = tagPositionsMap.value.get(a) || Infinity
    const positionB = tagPositionsMap.value.get(b) || Infinity
    return positionA - positionB
  })
})

const selectedTag = ref<string>()

const externalSearch = injectExperiencesSearch()
const searchTerm = externalSearch?.searchTerm ?? ref<string>()

watchEffect(() => {
  if (externalSearch) externalSearch.hasTags.value = tags.value.length > 0
})

const hasFilterBar = computed(() => tags.value.length > 0 || (searchable && !externalSearch))

watch(selectedTag, () => {
  searchTerm.value = ''
  page.value = 1
})

const filteredExperiences = computed(() => {
  let experiences = data.value?.data || []

  if (selectedTag.value) {
    experiences = experiences.filter(collection => collection.tags.includes(selectedTag.value))
  }

  if (searchTerm.value) {
    experiences = experiences.filter((collection) => {
      return collection.name.toLowerCase().includes(searchTerm.value.toLowerCase())
    })
  }

  return experiences
})

const pagedExperiences = computed(() => filteredExperiences.value.slice((page.value - 1) * perPage, page.value * perPage))

const hasExperiences = computed(() => filteredExperiences.value.length > 0)

const totalPages = computed(() => {
  if (!filteredExperiences.value) return 0
  return Math.ceil(filteredExperiences.value.length / perPage)
})
</script>
